import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["select", "results", "hiddenInputs"]
  static values = {
    // Hover configuration (optional - have sensible defaults)
    hoverEnabled: { type: Boolean, default: true },
    hoverChildSelector: { type: String, default: '.child-item' },
    hoverDataAttribute: { type: String, default: 'data-child' },

    // AJAX configuration (required)
    ajaxUrl: String, // REQUIRED: URL for AJAX requests

    // Data field mappings - REQUIRED parent fields, optional child fields with universal defaults
    parentIdField: String, // REQUIRED: e.g., 'overlayId', 'districtId', 'categoryId'
    parentNameField: String, // REQUIRED: e.g., 'overlayName', 'districtName', 'categoryName'
    childIdField: { type: String, default: 'id' },
    childTextField: { type: String, default: 'text' },
    childColorField: { type: String, default: 'color' },

    // Carousel button configuration (REQUIRED)
    carouselItems: String, // REQUIRED: JSON array like '[{"text": "Option 1", "value": "val1"}]'
    carouselDefaultValue: String, // REQUIRED: Default value matching one of the carouselItems values

    // Form field naming configuration (REQUIRED)
    fieldNamePrefix: String, // REQUIRED: e.g., 'MapOverlayShape', 'SchoolDistrict', 'ProductCategory'
    fieldNameKey: String, // REQUIRED: e.g., 'map_overlay', 'school_district', 'product_category'
    operatorFieldName: { type: String, default: 'operator' },
    valueFieldName: { type: String, default: 'value' },

    // Operator ID prefix (REQUIRED)
    operatorIdPrefix: String, // REQUIRED: e.g., 'mapOverlayOperator', 'districtOperator'

    // Event names for custom events (REQUIRED to avoid conflicts)
    eventNamespace: String, // REQUIRED: e.g., 'overlayShape', 'district', 'product'

    // Additional event hook for carousel button clicks (REQUIRED if using carousel buttons)
    carouselClickAction: String, // REQUIRED: e.g., 'click->search-filters#operatorClicked'

    // Select2 configuration (optional - have sensible defaults)
    minimumInputLength: { type: Number, default: 1 },
    useAjax: { type: Boolean, default: true }
  }

  connect() {
    // Initialize group modes from pre-rendered HTML
    this.groupModes = {}

    // Parse carousel items to get mode labels
    try {
      this.carouselItemsData = JSON.parse(this.carouselItemsValue)
      if (!Array.isArray(this.carouselItemsData)) {
        throw new Error('carouselItemsValue must be a JSON array')
      }
    } catch (error) {
      console.error(`Failed to parse carouselItemsValue: ${this.carouselItemsValue}`, error)
      // Fall back to overlay/shape related carousel items as a the default.
      this.carouselItemsData = [
        { text: 'Within', value: 'or' },
        { text: 'Not Within', value: 'not' },
        { text: 'Intersection', value: 'and' }
      ]
    }
    this.modeLabels = this.carouselItemsData.map(item => item.text)

    this.initializeGroupModesFromHTML()

    // Initialize data on pre-rendered options so select2 can access parent info
    this.initializePrerenderedOptionsData()

    // Define dropdownParent like the working examples
    var $dropdownParent = $('#filterModal').length === 0 ? $(document.body) : $('#filterModal')

    // Build select2 configuration
    const select2Config = {
      unmodified: true,
      dropdownParent: $dropdownParent,
      minimumInputLength: this.minimumInputLengthValue,
      templateResult: this.formatResults.bind(this),
      templateSelection: this.formatResults.bind(this)
    }

    // Add AJAX configuration if enabled and URL is provided
    if (this.useAjaxValue && this.ajaxUrlValue) {
      select2Config.ajax = {
        url: this.ajaxUrlValue,
        dataType: 'json',
        data: this.buildAjaxDataFunction(),
        processResults: this.buildAjaxProcessResultsFunction()
      }
    }

    // Initialize select2
    $(this.selectTarget).flexSelect2(select2Config)

    $(this.selectTarget).on('select2:open', function() {
      var $search = $(this).parent().find('.select2-search__field');
      $search.off('keydown').on('keydown', function(e) {
        let searchText = $(this).val() || '';
        let isBackspace = e.key === 'Backspace';
        let isDelete = e.key === 'Delete';

        // If there's no text and user presses backspace or delete, prevent select2 from deleting items.
        if (searchText.length === 0 && (isBackspace || isDelete)) {
          return false;
        }

        // Otherwise, allow the default behavior
        return true;
      })
    })

    // Handle individual item selection
    $(this.selectTarget).on('select2:select', (e) => {
      const itemData = e.params.data

      // Add the item to display
      this.addItemToDisplay(itemData)

      // Update hidden inputs
      this.updateHiddenInputsFromSelect2Data($(this.selectTarget).select2('data'))

      // Dispatch event
      this.dispatchCustomEvent('ItemAdded', {
        item: itemData,
        parentId: itemData[this.parentIdFieldValue],
        childId: itemData[this.childIdFieldValue]
      })
    })

    // Handle individual item removal
    $(this.selectTarget).on('select2:unselect', (e) => {
      const itemData = e.params.data
      const childId = itemData[this.childIdFieldValue]
      const parentId = itemData[this.parentIdFieldValue]

      // Remove from display
      this.removeChildFromDisplay(childId)

      // Update hidden inputs
      this.updateHiddenInputsFromSelect2Data($(this.selectTarget).select2('data'))

      // Dispatch event with full item data so handlers can check flags like notFromLocations
      this.dispatchCustomEvent('ItemDeleted', {
        item: itemData,
        parentId: parentId,
        childId: childId
      })
    })

    // Add hover event listeners if enabled
    if (this.hoverEnabledValue) {
      this.setupHoverEvents()
    }

    // Initialize carousel buttons for pre-rendered groups
    this.initializeCarouselButtons()
  }

  // Build the ajax data function from configuration or use default
  buildAjaxDataFunction() {
    // If you have a special case that does not follow the Name Eq '*<searchString>*', you would define that here.
    //if (this.eventNamespaceValue === 'MyUniqueNameSpace') {
    //  return function(params) {
    //    return {
    //      // Your custom filter logic here
    //      _filter: "Name Eq '*" + params.term + "*'"
    //    }
    //  }
    //}

    // This is how overlays/overlay shapes are looked up.  This is the initial use case for
    // this controller.  If this is not sufficent, further work will need to be one in order
    // for this controller to support the expaned needs.
    return function (params) {
      return {
        _filter: "Name Eq '*" + params.term + "*'",
        term: params.term
      }
    }
  }

  // Build the ajax processResults function from configuration or use default
  buildAjaxProcessResultsFunction() {
    const self = this

    // Special case: Overlay shapes - use hardcoded function to avoid security risk
    // It would be nice to use a more generic function here.  But as of now, not sure
    // how to do that.  This is our MVP solution.
    if (this.eventNamespaceValue === 'overlayShape') {
      return function(data) {
        return {
          results: data.map(function(shape) {
            return {
              id: "'" + shape.Id + "'",
              text: shape.Name,
              color: "#" + shape.Color,
              overlayId: shape.OverlayId,
              overlayName: shape.OverlayName || shape.OverlayId,
              notFromLocations: true
            }
          })
        }
      }
    }

    // Default function for other use cases - assumes the data structure matches the field mappings
    return function (data) {
      return {
        results: data.map(item => ({
          id: item.Id || item.id,
          text: item.Name || item.name || item.text,
          [self.parentIdFieldValue]: item[self.parentIdFieldValue],
          [self.parentNameFieldValue]: item[self.parentNameFieldValue],
          [self.childColorFieldValue]: item.Color || item.color
        }))
      }
    }
  }

  initializeGroupModesFromHTML() {
    // Read the operator values from the pre-rendered carousel buttons
    this.resultsTarget.querySelectorAll('.c-carousel-button.has-shapes-operator').forEach(button => {
      const parentId = button.id.replace(`${this.operatorIdPrefixValue}-`, '')
      const operator = button.getAttribute('data-active')

      // Map operator to mode index using carousel items
      const modeIndex = this.carouselItemsData.findIndex(item => item.value === operator)
      this.groupModes[parentId] = modeIndex >= 0 ? modeIndex : 0
    })
  }

  initializePrerenderedOptionsData() {
    // Ensure pre-rendered options have their data properly attached for select2
    // This allows select2 to access parent information (overlayId, overlayName, etc.)
    $(this.selectTarget).find('option').each((index, option) => {
      const $option = $(option)
      const dataObj = {
        [this.childIdFieldValue]: $option.val(),
        [this.childTextFieldValue]: $option.text(),
        [this.childColorFieldValue]: $option.data(this.childColorFieldValue),
        [this.parentIdFieldValue]: $option.data(this.parentIdFieldValue),
        [this.parentNameFieldValue]: $option.data(this.parentNameFieldValue)
      }
      // Attach the full data object to the option element
      $option.data('data', dataObj)
    })
  }

  initializeCarouselButtons() {
    // Initialize all pre-rendered carousel buttons
    this.resultsTarget.querySelectorAll('.c-carousel-button.has-shapes-operator').forEach(button => {
      this.initializeCarouselButton(button.id)
    })
  }

  // Handle custom event from external sources (e.g., locationSearch)
  handleAddItemEvent(event) {
    this.addItem(event.detail)
  }

  disconnect() {
    // Event listeners are automatically cleaned up when the controller disconnects
    // since they're bound to this.resultsTarget which will be removed from the DOM
  }

  // Public method to add an item from external sources (e.g., locationSearch or the map)
  addItem(itemData) {
    const itemId = itemData[this.childIdFieldValue]

    // Dont duplicate the item in the grouped select.
    // This can happen when the filter container is unchecked (dont_serialize) but
    // the UI keeps the selection, and the same item is added again from the map.
    const currentVals = $(this.selectTarget).val() || []
    if (currentVals.includes(itemId) && this.isChildDisplayed(itemId)) {
      return
    }

    this.ensureOptionExists(itemData, itemId)
    this.ensureOptionSelected(itemId)

    const allData = this.updateSelect2DataWithItem(itemData, itemId)
    this.addItemToDisplay(itemData)
    this.updateHiddenInputsFromSelect2Data(allData)

    // Dispatch event for programmatically added items
    this.dispatchCustomEvent('ItemAdded', {
      item: itemData,
      parentId: itemData[this.parentIdFieldValue],
      childId: itemId
    })
  }

  // Check if a child is already displayed (avoid duplicate DOM entries)
  isChildDisplayed(childId) {
    // Avoid attribute selectors with untrusted IDs; compare attributes directly.
    for (const el of this.resultsTarget.querySelectorAll('.child-item')) {
      if (el.getAttribute('data-child') === childId) return true
    }
    return false
  }

  // Ensure the option exists in the select element
  ensureOptionExists(itemData, itemId) {
    // Use filter() instead of attribute selector to avoid selector injection
    const optionExists = $(this.selectTarget).find('option').filter(function() {
      return $(this).val() === itemId
    }).length > 0
    if (!optionExists) {
      const newOption = new Option(itemData[this.childTextFieldValue], itemId, true, true)
      $(newOption).data('data', itemData)
      $(this.selectTarget).append(newOption)
    }
  }

  // Ensure the option is selected in Select2
  ensureOptionSelected(itemId) {
    const currentVals = $(this.selectTarget).val() || []
    if (!currentVals.includes(itemId)) {
      $(this.selectTarget).val([...currentVals, itemId])
    }
  }

  // Update Select2 data array with new or updated item
  updateSelect2DataWithItem(itemData, itemId) {
    const allData = this.getSelect2Data()
    const existingIndex = allData.findIndex(item => item[this.childIdFieldValue] === itemId)

    if (existingIndex === -1) {
      allData.push(itemData)
    } else {
      allData[existingIndex] = itemData
    }

    return allData
  }

  // Format the results for the select2 dropdown portion of grouped select2
  // Basically, show the color, name and parent name associated with the item
  formatResults(item) {
    if (!item.id) return item.text

    const parentDisplay = this.getParentDisplayText(item)
    const colorValue = item[this.childColorFieldValue] || '#cccccc'
    const itemText = item[this.childTextFieldValue] || item.text

    return $(`
      <div class='select2-result-repository clearfix'>
        <span>
          <span class="color-circle" style="background:${colorValue};margin-right:6px;vertical-align:middle;display:inline-block;width:12px;height:12px;border-radius:50%"></span>
          <strong>${itemText}</strong>
          <span style="color:#888; margin-left:8px; font-size:12px;">${parentDisplay}</span>
        </span>
      </div>
    `)
  }

  // Get display text for parent information
  getParentDisplayText(item) {
    const parentIdField = this.parentIdFieldValue
    const parentNameField = this.parentNameFieldValue
    const parentName = item[parentNameField]
    const parentId = item[parentIdField]

    if (parentName && parentName !== parentId) {
      return `(${parentName})`
    }
    return parentId ? `(${parentId})` : ''
  }

  updateHiddenInputs(groups) {
    // Clear existing inputs safely without using innerHTML
    while (this.hiddenInputsTarget.firstChild) {
      this.hiddenInputsTarget.removeChild(this.hiddenInputsTarget.firstChild)
    }

    Object.values(groups).forEach(group => {
      // Validate parentId - must be exactly 26 digits to prevent form field name injection
      const parentId = String(group.parentId || '')
      if (!this.isValidId(parentId)) {
        console.error(`Invalid parentId: ${group.parentId} - must be exactly 26 digits`)
        return
      }

      const modeIndex = this.groupModes[group.parentId] || 0
      const operator = this.getOperatorFromMode(modeIndex)
      const parentKey = `${this.fieldNameKeyValue}_${parentId}`

      // Create operator input
      const operatorInput = document.createElement('input')
      operatorInput.type = 'hidden'
      operatorInput.name = `search_fields[${this.fieldNamePrefixValue}][${parentKey}][${this.operatorFieldNameValue}]`
      operatorInput.value = operator
      this.hiddenInputsTarget.appendChild(operatorInput)

      // Create value inputs for each child
      group.children.forEach(child => {
        let childId = child[this.childIdFieldValue].toString().replace(/^'|'$/g, '')
        childId = String(childId || '')
        // Validate id - must be exactly 26 digits.  But the childId may be wrapped in single quotes.
        if (!this.isValidId(childId.replaceAll("'", ""))) {
          console.error(`Invalid childId: ${child[this.childIdFieldValue]} - must be exactly 26 digits`)
          return
        }

        const valueInput = document.createElement('input')
        valueInput.type = 'hidden'
        valueInput.name = `search_fields[${this.fieldNamePrefixValue}][${parentKey}][${this.valueFieldNameValue}][]`
        valueInput.value = childId
        this.hiddenInputsTarget.appendChild(valueInput)
      })
    })
  }

  getOperatorFromMode(modeIndex) {
    if (modeIndex >= 0 && modeIndex < this.carouselItemsData.length) {
      return this.carouselItemsData[modeIndex].value
    }
    return this.carouselDefaultValueValue
  }

  // Dispatch custom events with event namespace
  dispatchCustomEvent(eventSuffix, detail) {
    const eventName = `${this.eventNamespaceValue}${eventSuffix}`
    const customEvent = new CustomEvent(eventName, {
      detail: detail,
      bubbles: true,
      cancelable: true
    })
    this.element.dispatchEvent(customEvent)
  }

  // Setup hover events - dispatches custom events
  setupHoverEvents() {
    // Use event delegation to handle dynamically added child items
    this.resultsTarget.addEventListener('mouseover', (event) => {
      this.handleHoverEvent(event, 'start')
    })

    this.resultsTarget.addEventListener('mouseout', (event) => {
      this.handleHoverEvent(event, 'finish')
    })
  }

  // Handle hover events for both child items and group headers
  handleHoverEvent(event, hoverType) {
    // Check if the target itself is a child item
    if (event.target.classList.contains('child-item')) {
      this.dispatchHoverEvent(event.target, hoverType)
      return
    }

    // Otherwise, look for the closest child item
    const childItem = event.target.closest(this.hoverChildSelectorValue)
    if (childItem) {
      this.dispatchHoverEvent(childItem, hoverType)
    }

    // Also check for group header hover
    const groupHeader = event.target.closest('.group-header')
    if (groupHeader) {
      this.dispatchGroupHoverEvent(groupHeader, hoverType)
    }
  }

  // Dispatch custom hover event for child items
  dispatchHoverEvent(childItem, hoverType) {
    const childId = this.getItemId(childItem)
    const parentId = this.getParentId(childItem)

    if (childId && parentId) {
      const eventSuffix = hoverType === 'start' ? 'ItemMouseIn' : 'ItemMouseOut'
      this.dispatchCustomEvent(eventSuffix, {
        parentId: parentId,
        childId: childId,
        hoverType: hoverType,
        element: childItem,
        controller: this
      })
    }
  }

  // Dispatch custom hover event for group headers
  dispatchGroupHoverEvent(groupHeader, hoverType) {
    const parentId = groupHeader.getAttribute('data-parent')

    if (parentId) {
      const eventSuffix = hoverType === 'start' ? 'GroupMouseIn' : 'GroupMouseOut'
      this.dispatchCustomEvent(eventSuffix, {
        parentId: parentId,
        childId: null, // null indicates group header hover
        hoverType: hoverType,
        element: groupHeader,
        controller: this
      })
    }
  }

  // Utility methods for getting IDs from elements

  // Method to get the item ID from a child element
  getItemId(childItem) {
    return childItem.getAttribute(this.hoverDataAttributeValue)
  }

  // Method to get the parent ID from a child element
  getParentId(childItem) {
    return childItem.getAttribute('data-parent')
  }

  // Incremental update method - adds a single item to the display
  addItemToDisplay(itemData) {
    const parentId = itemData[this.parentIdFieldValue]
    const existingGroup = this.resultsTarget.querySelector(`[data-group-parent="${parentId}"]`)

    if (existingGroup) {
      // Group already exists, just add the child item
      this.addChildToExistingGroup(existingGroup, itemData)
    } else {
      // Group doesn't exist, create new group with this item
      this.createNewGroup(itemData)
    }
  }

  // Add a child item to an existing group
  addChildToExistingGroup(groupElement, itemData) {
    // Guard against duplicates: select2 data updates can be called multiple times from external sources.
    if (this.isChildDisplayed(itemData[this.childIdFieldValue])) {
      return
    }
    const childElement = this.buildChildItemElement(itemData)
    const groupContainer = groupElement.closest('.group-container')
    const childrenContainer = groupContainer.querySelector('.group-children')
    childrenContainer.appendChild(childElement)
  }

  // Build DOM element for a single child item (safe from XSS)
  buildChildItemElement(itemData) {
    const childId = itemData[this.childIdFieldValue]
    const childText = itemData[this.childTextFieldValue]
    const childColor = itemData[this.childColorFieldValue] || '#cccccc'
    const parentId = itemData[this.parentIdFieldValue]

    // Create the main container div
    const childDiv = document.createElement('div')
    childDiv.className = 'child-item'
    childDiv.setAttribute('data-child', childId)
    childDiv.setAttribute(this.hoverDataAttributeValue, childId)
    childDiv.setAttribute('data-parent', parentId)

    // Create color circle span
    const colorCircle = document.createElement('span')
    colorCircle.className = 'color-circle'
    colorCircle.style.background = this.sanitizeColor(childColor)
    childDiv.appendChild(colorCircle)

    // Create child name span (using textContent to prevent XSS)
    const childName = document.createElement('span')
    childName.className = 'child-name'
    childName.textContent = childText
    childDiv.appendChild(childName)

    // Create delete button
    const deleteButton = document.createElement('button')
    deleteButton.type = 'button'
    deleteButton.className = 'delete-child'
    deleteButton.setAttribute('data-action', 'click->grouped-select2#deleteItem')
    deleteButton.setAttribute('data-child', childId)
    deleteButton.setAttribute('title', 'Remove item')
    deleteButton.textContent = '×'
    childDiv.appendChild(deleteButton)

    return childDiv
  }

  // Create a new group for an item
  createNewGroup(itemData) {
    const parentId = itemData[this.parentIdFieldValue]
    const parentName = itemData[this.parentNameFieldValue]
    // We are creating a new group.
    let modeIndex = this.groupModes[parentId] || 0
    if (modeIndex === 0){
      // If we are in the default mode (and we probably are because we are creating a new group)
      // check if the caller has a preference for the item operator and use that if available.
      modeIndex = itemData.newGroupOperator ? this.carouselItemsData.findIndex(item => item.value === itemData.newGroupOperator) : modeIndex;
      this.groupModes[parentId] = modeIndex;
    }
    const operatorId = `${this.operatorIdPrefixValue}-${parentId}`

    const groupElement = this.buildGroupElement(itemData, parentId, parentName, modeIndex, operatorId)
    this.resultsTarget.appendChild(groupElement)

    // Initialize the carousel button for the new group
    this.initializeCarouselButton(operatorId)
  }

  // Build DOM element for a complete group container (safe from XSS)
  buildGroupElement(itemData, parentId, parentName, modeIndex, operatorId) {
    const childElement = this.buildChildItemElement(itemData)
    const operator = this.getOperatorFromMode(modeIndex)

    // Create group container
    const groupContainer = document.createElement('div')
    groupContainer.className = 'group-container'
    groupContainer.setAttribute('data-group-parent', parentId)

    // Create group header
    const groupHeader = document.createElement('div')
    groupHeader.className = 'group-header'
    groupHeader.setAttribute('data-parent', parentId)

    // Create group name span (using textContent to prevent XSS)
    const groupName = document.createElement('span')
    groupName.className = 'group-name'
    groupName.setAttribute('title', parentName)
    groupName.textContent = parentName
    groupHeader.appendChild(groupName)

    // Create carousel button div
    const carouselButton = document.createElement('div')
    carouselButton.id = operatorId
    carouselButton.className = 'c-carousel-button has-shapes-operator'
    carouselButton.setAttribute('data-items', this.carouselItemsValue)
    carouselButton.setAttribute('data-active', operator)
    carouselButton.setAttribute('data-input-id', `search_fields_${this.fieldNameKeyValue}-${parentId}-operator`)
    carouselButton.setAttribute('data-input-name', `search_fields[${this.fieldNamePrefixValue}][${this.fieldNameKeyValue}_${parentId}][${this.operatorFieldNameValue}]`)
    carouselButton.setAttribute('data-button-style', 'min-width: 115px; margin-bottom: 0px;')
    carouselButton.setAttribute('data-action', this.carouselClickActionValue)
    groupHeader.appendChild(carouselButton)

    // Create delete group button
    const deleteGroupButton = document.createElement('button')
    deleteGroupButton.type = 'button'
    deleteGroupButton.className = 'delete-group'
    deleteGroupButton.setAttribute('data-action', 'click->grouped-select2#deleteGroup')
    deleteGroupButton.setAttribute('data-parent', parentId)
    deleteGroupButton.setAttribute('title', 'Delete group')
    deleteGroupButton.textContent = '×'
    groupHeader.appendChild(deleteGroupButton)

    groupContainer.appendChild(groupHeader)

    // Create group children container
    const groupChildren = document.createElement('div')
    groupChildren.className = 'group-children'
    groupChildren.appendChild(childElement)
    groupContainer.appendChild(groupChildren)

    return groupContainer
  }

  // Initialize a carousel button with consistent styling
  initializeCarouselButton(operatorId) {
    // Validate operatorId to prevent selector injection - only allow alphanumeric, hyphens, underscores
    if (!/^[a-zA-Z0-9_-]+$/.test(operatorId)) {
      console.error(`Invalid operatorId format: ${operatorId}`)
      return
    }

    const element = document.getElementById(operatorId)
    if (!element) {
      console.error(`Element with id "${operatorId}" not found`)
      return
    }

    new CarouselButton(`#${operatorId}`)
    const container = element.querySelector('.c-carousel-button-container')
    if (container) {
      $(container).css('justify-content', 'end')
    }
  }

  // Remove a child item from display
  removeChildFromDisplay(childId) {
    const childElement = this.resultsTarget.querySelector(`[data-child="${childId}"]`)
    if (!childElement) return

    const parentId = childElement.dataset.parent
    childElement.remove()

    // Remove group if it's now empty
    this.removeEmptyGroup(parentId)
  }

  // Remove a group container if it has no remaining children
  removeEmptyGroup(parentId) {
    const groupContainer = this.resultsTarget.querySelector(`[data-group-parent="${parentId}"]`)
    if (groupContainer) {
      const remainingChildren = groupContainer.querySelectorAll('.child-item')
      if (remainingChildren.length === 0) {
        groupContainer.remove()
      }
    }
  }

  // Update hidden inputs from current select2 data
  updateHiddenInputsFromSelect2Data(allData) {
    const groups = this.groupItemsByParent(allData)
    this.updateHiddenInputs(groups)
  }

  // Group items by their parent ID
  groupItemsByParent(items) {
    const groups = {}
    items.forEach(item => {
      const parentId = item[this.parentIdFieldValue]
      const parentName = item[this.parentNameFieldValue]

      if (!groups[parentId]) {
        groups[parentId] = {
          parentName: parentName,
          children: [],
          parentId: parentId
        }
      }
      groups[parentId].children.push(item)
    })
    return groups
  }

  deleteGroup(e) {
    const parentId = String(e.target.dataset.parent || '')
    // Validate parentId - must be exactly 26 digits
    if (!this.isValidId(parentId)) {
      console.error(`Invalid parentId in deleteGroup: ${parentId}`)
      return
    }

    const selectedData = this.getSelect2Data()
    const toRemove = selectedData
      .filter(item => item[this.parentIdFieldValue] === parentId)
      .map(item => item[this.childIdFieldValue])

    // Remove all items from Select2
    const selected = $(this.selectTarget).val() || []
    const newSelected = selected.filter(id => !toRemove.includes(id))
    $(this.selectTarget).val(newSelected)

    // Remove the entire group container
    const groupContainer = this.resultsTarget.querySelector(`[data-group-parent="${parentId}"]`)
    if (groupContainer) {
      groupContainer.remove()
    }

    // Update hidden inputs and trigger change
    this.updateHiddenInputsFromSelect2Data(this.getSelect2Data())
    this.triggerSelect2Change()

    // Dispatch custom event for group deleted
    this.dispatchCustomEvent('GroupDeleted', {
      parentId: parentId,
      removedItems: toRemove
    })
  }

  toggleMode(e) {
    const parentId = String(e.target.dataset.parent || '')
    // Validate parentId - must be exactly 26 digits
    if (!this.isValidId(parentId)) {
      console.error(`Invalid parentId in toggleMode: ${parentId}`)
      return
    }
    
    const currentMode = this.groupModes[parentId] ?? 0
    const newModeIndex = (currentMode + 1) % this.modeLabels.length

    this.groupModes[parentId] = newModeIndex
    this.updateCarouselButtonMode(parentId, newModeIndex)
    this.updateHiddenInputsFromSelect2Data(this.getSelect2Data())
    this.triggerSelect2Change()

    // Dispatch custom event for operator changed
    this.dispatchCustomEvent('OperatorChanged', {
      parentId: parentId,
      newOperator: this.getOperatorFromMode(newModeIndex),
      modeIndex: newModeIndex
    })
  }

  // Update carousel button display for a specific parent
  updateCarouselButtonMode(parentId, modeIndex) {
    const operatorId = `${this.operatorIdPrefixValue}-${parentId}`
    // Use getElementById instead of querySelector to avoid selector injection
    const carouselButton = document.getElementById(operatorId)

    if (!carouselButton) return

    carouselButton.setAttribute('data-active', this.getOperatorFromMode(modeIndex))

    // Update the carousel button display if instance exists
    if (carouselButton.carouselButtonInstance) {
      carouselButton.carouselButtonInstance.updateDisplay()
    }
  }

  deleteItem(e) {
    const childId = String(e.target.dataset.child || '')
    // Validate id - must be exactly 26 digits.  But the childId may be wrapped in single quotes.
    if (!this.isValidId(childId.replaceAll("'", ""))) {
      console.error(`Invalid childId in deleteItem: ${childId} - must be exactly 26 digits`)
      return
    }

    const childElement = this.resultsTarget.querySelector(`[data-child="${childId}"]`)

    // If element doesn't exist, it may have already been removed (race condition, external removal, etc.)
    if (!childElement) return

    const parentId = String(childElement.dataset.parent || '')
    // Validate parentId - must be exactly 26 digits
    if (!this.isValidId(parentId)) {
      console.error(`Invalid parentId in deleteItem: ${parentId}`)
      return
    }

    const select2Data = this.getSelect2Data()
    const itemData = select2Data.find(item => item[this.childIdFieldValue] === childId)

    this.removeItemFromSelect2(childId)
    this.removeChildFromDisplay(childId)
    this.updateHiddenInputsFromSelect2Data(
      select2Data.filter(item => item[this.childIdFieldValue] !== childId)
    )
    this.triggerSelect2Change()

    // Dispatch event with full item data so handlers can check flags like notFromLocations
    this.dispatchCustomEvent('ItemDeleted', {
      item: itemData,
      parentId: parentId,
      childId: childId
    })
  }

  // Handle custom remove event from external sources (e.g., locationSearch, map context menu)
  // This removes the item silently without dispatching events to prevent circular flows
  handleRemoveItemEvent(event) {
    const { childId } = event.detail
    const select2Data = this.getSelect2Data()

    this.removeItemFromSelect2(childId)
    this.removeChildFromDisplay(childId)
    this.updateHiddenInputsFromSelect2Data(
      select2Data.filter(item => item[this.childIdFieldValue] !== childId)
    )
    this.triggerSelect2Change()

    // Don't dispatch any events - this is a silent removal to keep in sync
  }

  // Helper methods for Select2 operations
  getSelect2Data() {
    return $(this.selectTarget).select2('data')
  }

  removeItemFromSelect2(childId) {
    const selected = $(this.selectTarget).val() || []
    const newSelected = selected.filter(id => id !== childId)
    $(this.selectTarget).val(newSelected)
  }

  triggerSelect2Change() {
    $(this.selectTarget).trigger('change')
  }

  // Validate and sanitize CSS color values to prevent CSS injection
  // Assumes colors will always be in hex format (#rgb or #rrggbb)
  // Returns a safe color value or a default fallback
  sanitizeColor(color) {
    if (!color || typeof color !== 'string') {
      return '#cccccc'
    }

    const trimmedColor = color.trim()

    // Only allow hex colors (#rrggbb or #rgb) - prevents CSS injection
    if (/^#[0-9A-Fa-f]{3}$|^#[0-9A-Fa-f]{6}$/.test(trimmedColor)) {
      return trimmedColor
    }

    // If color doesn't match hex pattern, return default
    return '#cccccc'
  }

  isValidId(id) {
    // Validate 26 digit Id?
    return /^\d{26}$/.test(id)
  }


}